<template>
  <div>
    <!-- Submenú en la parte superior del card -->
    <div class="sub-menu-wrapper">
      <SubMenu
        :items="MenuOptions.contexts.samples.itemsByArea.agronomy"
      />
    </div>
    <div class="header-container">
      <span class="header-title">{{ $t('comparative') }}</span>
    </div>
    <div class="grid-wrapper">
      <DxDataGrid
        :data-source="comparatorDs"
        :show-borders="false"
        :show-column-lines="false"
        :allow-column-reordering="false"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :show-column-headers="true"
        :show-colon="false"
        :remote-operations="true"
        :hover-state-enabled="false"
        :show-row-lines="false"
        :row-alternation-enabled="false"
        class="custom-grid custom-grid-comparator"
        @toolbar-preparing="onToolbarPreparing($event)"
      >
        <DxScrolling mode="standard" />
        <DxStateStoring
          :enabled="false"
          type="localStorage"
          storage-key="gisComparatorTable"
        />
        <DxPaging :page-size="10" />
        <DxPager
          :show-page-size-selector="true"
          :allowed-page-sizes="[5, 10, 20]"
          :show-info="true"
          info-text="{2} items"
          :visible="true"
        />
        <DxSearchPanel
          :visible="true"
          :width="300"
          :placeholder="$t('search')"
        />
        <DxExport :enabled="false" />

        <DxColumn
          data-field="farm"
          :allow-header-filtering="false"
          :caption="$t('agronomySample.summary.estate')"
          alignment="left"
        />
        <DxColumn
          data-field="parcel"
          :allow-header-filtering="false"
          :caption="$t('agronomySample.summary.parcel')"
          alignment="left"
        />
        <DxColumn
          data-field="cultive"
          :allow-header-filtering="false"
          :caption="$t('agronomySample.cultive')"
          alignment="left"
        />
        <DxColumn
          data-field="variety"
          :allow-header-filtering="false"
          :caption="$t('variety')"
          alignment="left"
        />
        <DxColumn
          data-field="attendant"
          :allow-header-filtering="false"
          :caption="$t('accountable')"
          alignment="left"
        />
        <DxColumn
          data-field="campaign"
          :allow-header-filtering="false"
          :caption="$t('campaign')"
          alignment="left"
        />
        <DxColumn
          data-field="last_sampling"
          :caption="$t('lastSampling')"
          alignment="left"
          data-type="date"
          :format="$helpers.getDateFormat(company)"
          :allow-header-filtering="false"
        />
        <DxColumn
          data-field=""
          alignment="center"
          :caption="$t('left')"
          :allow-header-filtering="false"
          cell-template="leftComparatorTemplate"
        />
        <DxColumn
          data-field=""
          alignment="center"
          :caption="$t('right')"
          :allow-header-filtering="false"
          cell-template="rightComparatorTemplate"
        />

        <template #leftComparatorTemplate="{ data: farm }">
          <div>
            <DxCheckBox
              :value="farm.data.leftComparator"
              @valueChanged="comparatorChanged($event, 'left', farm)"
            />
          </div>
        </template>
        <template #rightComparatorTemplate="{ data: farm }">
          <div>
            <DxCheckBox
              :value="farm.data.rightComparator"
              @valueChanged="comparatorChanged($event, 'right', farm)"
            />
          </div>
        </template>
      </DxDataGrid>

      <!-- Botón superpuesto, “dentro” visualmente de la toolbar -->
      <div class="toolbar-right-slot">
        <f7-button
          class="botones-registrar"
          style="width: 109px"
          type="default"
          styling-mode="contained"
          :disabled="leftComparator === '' || rightComparator === ''"
          @click="goToComparative"
        >
          {{ $t('comparative') }}
        </f7-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxScrolling,
  DxExport,
} from 'devextreme-vue/data-grid';
import MenuOptions from '@/json/menuOptions.json';
import SubMenu from '@/components/SubMenu/index.vue';
import { mapActions, mapState } from 'vuex';
import { DxCheckBox } from 'devextreme-vue/check-box';

export default {
  name: 'GisComparatorTable',
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxStateStoring,
    DxPager,
    DxPaging,
    DxScrolling,
    DxExport,
    DxCheckBox,
    SubMenu,
  },
  data() {
    return { MenuOptions };
  },
  computed: {
    ...mapState('contact', ['company']),
    ...mapState('gisComparator', [
      'comparatorDs',
      'leftComparator',
      'rightComparator',
    ]),
  },
  methods: {
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = { class: 'custom-toolbar' };
      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') item.location = 'before';
      });

      toolbarItems.push({
        name: 'Filtros',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('filter'),
          icon: '././static/icons/ic_filter.svg',
          width: 115,
          elementAttr: { class: 'btn-filtrar-custom' },
          onClick: () => this.$f7.panel.open('right'),
        },
      });
    },
    comparatorChanged(e, comparator, farm) {
      if (e.event) {
        if (comparator === 'left') {
          this.setLeftParcel({
            coordinates: farm.data.coordinates,
            name: farm.data.parcel,
            id: farm.data.parcel_id,
            gisId: farm.data.gis_id || farm.data.parcel_id,
          });
          this.updateLeftComparator(farm.data.parcel_id);
        } else {
          this.setRightParcel({
            coordinates: farm.data.coordinates,
            name: farm.data.parcel,
            id: farm.data.parcel_id,
            gisId: farm.data.gis_id || farm.data.parcel_id,
          });
          this.updateRightComparator(farm.data.parcel_id);
        }
      }
    },
    goToComparative() {
      this.$f7router.navigate(
        { name: 'TimeDimensionComparatorPage' },
        { animate: true, transition: 'f7-fade' }
      );
    },
    ...mapActions('gisComparator', [
      'updateLeftComparator',
      'updateRightComparator',
      'setLeftParcel',
      'setRightParcel',
    ]),
  },
};
</script>

<style>
.header-container {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.header-title {
  color: var(--Primary-Blue-60, #04509f);
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  display: flex;
  align-items: center;
  padding-top: 15px;
}

.grid-wrapper {
  position: relative;
}

.toolbar-right-slot {
  position: absolute !important;
  top: 40px !important;
  right: 16px !important;
  z-index: 2 !important; /* por encima del grid */
  pointer-events: auto !important; /* el botón debe ser clicable */
}
.custom-grid-comparator .dx-toolbar-before {
  display: grid;
  grid-template-columns: 106px 100px 38px 115px;
  grid-template-rows: repeat(1, 1fr);
  row-gap: 24px;
  align-items: center;
  box-sizing: border-box;
}
.custom-grid-comparator .dx-toolbar-before :nth-child(1) {
  grid-row: 1;
  grid-column: 1 / span 2;
}
.custom-grid-comparator .dx-toolbar-before :nth-child(2) {
  grid-row: 1;
  grid-column: 4;
}
.btn-filtrar-custom {
  border-radius: 10px !important;
  border: 1px solid var(--Primary-Blue-70, #00346a) !important;
  color: var(--Primary-Blue-70, #00346a) !important;
  background: var(--Basic-White, #fff) !important;
  margin-bottom: 2% !important;
}

.btn-filtrar-custom:hover {
  border-radius: 10px !important;
  background: var(--Primary-Blue-10, #ebf2fa) !important;
  border: 1px solid var(--Primary-Blue-70, #00346a) !important;
  box-shadow: 0 1px 4px 0 rgba(87, 84, 95, 0.3),
    0 1px 4px 0 rgba(87, 84, 95, 0.15) !important;
}
</style>
